import React from 'react';
import {FileEntity} from '@pages/FilesPage';
import {Chip, Stack, Typography} from '@mui/material';
import {DT_FORMATS, formatDate} from 'utils';
import {FileTagChip} from './FileTagChip';
import {useDeviceInfo} from 'utils/hooks/useDeviceInfo';
import {LightTooltip} from '@ui/LightTooltip/LightTooltip';
import {useTranslation} from 'react-i18next';

type FileCellProps = {
    record: FileEntity;
};

export const FileCell = ({record}: FileCellProps) => {
    const {isDesktop, isTablet} = useDeviceInfo();

    let maxVisibleTags = 4;
    if (isTablet) {
        maxVisibleTags = 6;
    }
    if (isDesktop) {
        maxVisibleTags = 10;
    }

    const tags = record.tags || [];
    const visibleTags = tags.slice(0, maxVisibleTags);
    const hiddenTags = tags.slice(maxVisibleTags);

    const {t} = useTranslation('common');

    return (
        <Stack
            sx={{
                width: '100%',
                textAlign: 'left',
                padding: 1,
                justifyContent: 'flex-start',
                alignItems: 'flex-start',
                flexDirection: 'column',
                display: 'flex',
            }}
        >
            <Stack
                direction='column'
                width='100%'
            >
                <Stack
                    direction='row'
                    alignItems='center'
                    gap={2}
                >
                    <Typography
                        variant='button'
                        color='inherit'
                        sx={{justifyContent: 'flex-start'}}
                    >
                        {record.originalFilename}
                    </Typography>
                    {record.documentDate && (
                        <Stack
                            direction='row'
                            alignItems='end'
                        >
                            <Typography
                                variant='caption'
                                sx={{opacity: '.56', paddingRight: 1}}
                            >
                                {t('pretext.from2').toLowerCase()}
                            </Typography>
                            <Typography fontSize='16px'>
                                {`${formatDate(record.documentDate, DT_FORMATS.defaultDate, true)}`}
                            </Typography>
                        </Stack>
                    )}
                </Stack>
                <Stack
                    direction='row'
                    alignItems='center'
                    width='100%'
                    display='grid'
                    gridTemplateColumns='200px 100px auto'
                >
                    <Typography
                        variant='caption'
                        sx={{opacity: '.56'}}
                    >
                        {`${t('uploaded1')} ${formatDate(
                            record.uploadDate,
                            DT_FORMATS.defaultDate,
                        )} ${t('pretext.atTime').toLowerCase()} ${formatDate(record.uploadDate, DT_FORMATS.shortTime)}`}
                    </Typography>

                    <Typography
                        variant='caption'
                        sx={{opacity: '.56'}}
                    >
                        {`${Math.round(record.fileSize * 0.001)} kb`}
                    </Typography>
                    <Stack
                        direction='row'
                        gap={1}
                        flexWrap='wrap'
                    >
                        {visibleTags.map((tag, index) => (
                            <FileTagChip
                                key={index}
                                tag={tag}
                            />
                        ))}
                        {hiddenTags.length > 0 && (
                            <LightTooltip
                                title={
                                    <Stack
                                        direction='row'
                                        gap={1}
                                        flexWrap='wrap'
                                    >
                                        {hiddenTags.map((tag, i) => (
                                            <FileTagChip
                                                key={i}
                                                tag={tag}
                                            />
                                        ))}
                                    </Stack>
                                }
                            >
                                <Chip
                                    label={`+${hiddenTags.length}`}
                                    size='small'
                                    color='primary'
                                    variant='outlined'
                                    sx={{
                                        cursor: 'pointer',
                                        borderRadius: '100px',
                                    }}
                                />
                            </LightTooltip>
                        )}
                    </Stack>
                </Stack>
            </Stack>
        </Stack>
    );
};
